import { Injectable } from '@angular/core';
import {
  ApiBoardSettingsResponse,
  ApiCreateFeedbackSettingsRequest,
  ApiCreateStoryRequest,
  ApiDashboardSettingsResponse,
  ApiFeedbackSettingsResponse,
  ApiLinkingImageResponse,
  ApiListActionOriginResponse,
  ApiListActionStatusesResponse,
  ApiListActionTagsResponse,
  ApiListActionTypesResponse,
  ApiListReductionGoalResponse,
  ApiListStakeholdersResponse,
  ApiListTransitionStoryGroupsResponse,
  ApiStoryResponse,
  ApiUpdateActionStatusRequest,
  ApiUpdateActionTagRequest,
  ApiUpdateActionTypeRequest,
  ApiUpdateDashboardSettingsRequest,
  ApiUpdateFeedbackSettingsRequest,
  ApiUpdateStakeholderRequest,
  ApiUpdateStoryRequest,
  ApiUpdateV3RequiredFieldsRequest,
  ApiV3RequiredFieldsResponse,
  ModelsActionTagColour,
  ApiUpdateBoardRequest as UpdateBoardRequest,
} from '../../api/core/models';
import { ActionResponse } from '../../api/core/models/api/action-response';
import { BoardResponse } from '../../api/core/models/api/board-response';
import { CreateActionRequest } from '../../api/core/models/api/create-action-request';
import { CreateStoryRequest } from '../../api/core/models/api/create-story-request';
import { UpdateActionRequest } from '../../api/core/models/api/update-action-request';
import { UpdateBoardSettingsRequest } from '../../api/core/models/api/update-board-settings-request';
import { WatchlistResponse } from '../../api/core/models/api/watchlist-response';
import { ApiTransitionElementHierarchyResponse } from '../../api/data-definitions/models';
import { EMPTY_GUID } from '../../constants/empty-guid';
import { isNotNullOrUndefined } from '../../functions/is-not-null-or-undefined';
import { conditionallyPrefixImagesWithFilesUrl } from '../../functions/utilities/img-url-utility';
import { ClientAction } from '../../model/client-model/client-action';
import { ClientBoard } from '../../model/client-model/client-board';
import { ActionProperties } from '../../model/client-model/properties/action.properties';
import { RAGRating } from '../../model/client-model/properties/rag-rating';
import { Layout } from '../../model/client-model/settings/board-settings/layout';
import { DefaultActionsView } from '../../model/default-actions-view';
import { Currency } from '../../model/enums/currency';
import { Language } from '../../model/enums/language';
import { StakeholderWithCostDistribution } from '../../model/stakeholder-cost-distribution';
import { StoryFormGroup } from '../../modules/shared-editor/components/story/story.component';
import { AppConfigService } from '../app-config.service';
import { LoggingService } from '../logging.service';
import {
  apiBoardReductionGoalsToBoardReductionGoals,
  apiBoardSettingsToActionOrigins,
  apiBoardSettingsToActionTags,
  apiBoardSettingsToActionTypes,
  apiBoardSettingsToCurrency,
  apiBoardSettingsToMeasurementSystem,
  apiBoardSettingsToSwimlanes,
  apiBoardSettingsToTimeline,
  apiFeedBackSettingsToFeedbackSettings,
  dashboardSettingsToDefaultActionsView,
  dashboardSettingsToPointOfEntrySettings,
  displayUnitExponentToDisplayUnit,
  displayUnitToDisplayUnitExponent,
  emissionStoryToEmissionPathways,
  linkingTextsToNamedLinks,
  mainStoryToExtendedDescription,
} from './helpers';
import { apiBoardSettingsToElectricityEmissionFactorType } from './helpers/api-board-settings-to-electricity-emission-factor-type';
import { apiDashboardSettingsToPathwayGap } from './helpers/api-dashboard-settings-to-pathway-gap';
import { apiStakeholdersToStakeholders } from './helpers/api-stakeholders-to-stakeholders';
import { apiV3RequiredFieldsToV3RequiredFields } from './helpers/api-v3-required-fields-to-v3-required-fields';
import { emissionFactorCalculationMethodToApiElectricityEmissionFactorType } from './helpers/emission-factor-calculation-method-to-api-electricity-emission-factor-type';
import { measurementSystemToApiMeasurementSystem } from './helpers/measurement-system-to-api-measurement-system';
import { transitionElementLayoutToTransitionTargetNodes } from './helpers/transition-element-layout-to-transition-target-nodes';
import { ACTION_STATUS, BoardSettingsV2, ClientBoardSettings, ClientBoardV2, STAKEHOLDER_TYPE } from './model';
import { STAKEHOLDER_TYPE_ALLOWED } from './model/stakeholder-type';

@Injectable({
  providedIn: 'root',
})
export class ModelV2Service {
  constructor(
    private _config: AppConfigService,
    private _loggingService: LoggingService
  ) {}

  boardResponseToClientBoard({
    board,
    boardSettings,
    dashboardSettings,
    apiStakeholders,
    mainStory,
    emissionStory,
    apiReductionGoals,
    apiActionTags,
    apiActionTypes,
    apiActionOrigins,
    actionStatuses,
    apiFeedbackSettings,
    footerLogos,
    apiV3RequiredFields,
    consumptionHierarchy,
    supplyHierarchy,
  }: {
    board: BoardResponse;
    boardSettings: ApiBoardSettingsResponse;
    dashboardSettings: ApiDashboardSettingsResponse;
    apiStakeholders?: ApiListStakeholdersResponse;
    mainStory: ApiStoryResponse;
    emissionStory?: ApiStoryResponse;
    apiReductionGoals: ApiListReductionGoalResponse;
    apiActionTags?: ApiListActionTagsResponse;
    actionStatuses?: ApiListActionStatusesResponse;
    apiActionTypes?: ApiListActionTypesResponse;
    apiActionOrigins?: ApiListActionOriginResponse;
    apiFeedbackSettings?: ApiFeedbackSettingsResponse;
    footerLogos: ApiLinkingImageResponse[];
    apiV3RequiredFields: ApiV3RequiredFieldsResponse | undefined;
    consumptionHierarchy: ApiListTransitionStoryGroupsResponse;
    supplyHierarchy: ApiTransitionElementHierarchyResponse;
  }): ClientBoardV2 {
    const defaultLanguage = boardSettings.primaryLanguage as Language;
    const displayUnit = displayUnitExponentToDisplayUnit(this._loggingService, boardSettings.displayUnitExponent);
    const transitionTargetNodes = transitionElementLayoutToTransitionTargetNodes(dashboardSettings);
    const timeline = apiBoardSettingsToTimeline(this._loggingService, boardSettings);
    const feedbackSettings = apiFeedBackSettingsToFeedbackSettings(apiFeedbackSettings);
    const extendedDescription = mainStoryToExtendedDescription(mainStory);
    const pointOfEntrySettings = dashboardSettingsToPointOfEntrySettings(
      footerLogos,
      this._config.baseUrlFiles,
      dashboardSettings
    );
    const swimlanes = apiBoardSettingsToSwimlanes(actionStatuses);
    const actionTypes = apiBoardSettingsToActionTypes(apiActionTypes);
    const actionOrigins = apiBoardSettingsToActionOrigins(apiActionOrigins);

    const reductionGoals = apiBoardReductionGoalsToBoardReductionGoals(apiReductionGoals.reductionGoals);
    const measurementSystem = apiBoardSettingsToMeasurementSystem(boardSettings);
    const electricityEmissionFactorType = apiBoardSettingsToElectricityEmissionFactorType(boardSettings);
    const currency = apiBoardSettingsToCurrency(this._loggingService, boardSettings);
    const stakeholders = apiStakeholdersToStakeholders(apiStakeholders);
    const actionTags = apiBoardSettingsToActionTags(apiActionTags);
    const defaultActionsView = dashboardSettingsToDefaultActionsView(dashboardSettings);
    const emissionsPathways = emissionStoryToEmissionPathways(emissionStory);
    const pathwayGap = apiDashboardSettingsToPathwayGap(dashboardSettings);
    const childNodes = consumptionHierarchy.transitionStoryGroups?.map((group) => group.id) ?? [];
    const supplyChildNodes = supplyHierarchy.groups?.map((group) => group.id) ?? [];
    const v3RequiredFields = apiV3RequiredFieldsToV3RequiredFields(apiV3RequiredFields);

    const clientBoard: ClientBoardV2 = {
      title: board.title,
      childNodes,
      supplyChildNodes,
      id: board.id,
      modified: new Date(board.updatedAt).getTime(),
      isPrimary: board.isPrimary,
      ...(board.lastPublished ? { publishedAt: new Date(board.lastPublished).getTime() } : {}),
      boardSettings: {
        actionSettingsSubHeading: dashboardSettings?.actionSettingsSubHeading ?? '',
        actionSettingsDescription: dashboardSettings?.actionSettingsDescription ?? '',
        actionTypes,
        actionOrigins,
        activityHierarchy: boardSettings.activityHierarchy ?? '',
        internationalisation: {
          defaultLanguage,
        },
        estimatesLocale: boardSettings.estimatesLocale,
        displayUnit,
        transitionTargetNodes,
        timeline,
        ...(feedbackSettings && Object.keys(feedbackSettings).length ? { feedbackSettings } : {}),
        pointOfEntrySettings,
        swimlanes,
        reductionGoals,
        rootNodeProperties: {
          diagramHeader: mainStory?.diagramHeader ?? '',
        },
        measurementSystem,
        currency,
        stakeholders,
        showActionsCosts: dashboardSettings?.actionSettingsShowCosts ?? false,
        showUnconnectedActions: dashboardSettings?.actionSettingsShowUnconnected ?? false,
        yearByYearDiagramLayout: {
          showBauAndAbatement: dashboardSettings?.showReferenceScenarioAndAbatement ?? false,
        },
        actionTags,
        defaultActionsView,
        introductionSummary: dashboardSettings?.splashScreenDescription ?? undefined,
        introductionTitle: dashboardSettings?.splashScreenTitle ?? undefined,
        goalTitle: dashboardSettings?.goalTitle ?? undefined,
        emissionsPathways,
        extendedDescription,
        pathwayGap,
        ...v3RequiredFields,
        electricityEmissionFactorType,
        publicTabs: dashboardSettings.publicTabs,
        primaryActionGrouping: dashboardSettings.primaryActionGrouping,
        secondaryActionGrouping: dashboardSettings.secondaryActionGrouping,
        emissionsTabHideBracket: dashboardSettings.emissionsTabHideBracket ?? false,
        emissionsTabHideReference: dashboardSettings.emissionsTabHideReference ?? false,
        indicatorsHideContribution: dashboardSettings.indicatorsHideContribution ?? false,
        headerLogoUrl: dashboardSettings.headerLogoUrl,
      },
    };

    return clientBoard;
  }

  clientBoardToUpdateBoardRequest(clientBoard: ClientBoardV2 | ClientBoard): UpdateBoardRequest {
    return {
      ...(clientBoard.isPrimary ? { isPrimary: clientBoard.isPrimary } : {}),
      ...(clientBoard.title ? { title: clientBoard.title } : {}),
    };
  }

  clientBoardToUpdateBoardSettingsRequest(
    clientBoardSettings: ClientBoardV2['boardSettings']
  ): UpdateBoardSettingsRequest {
    let {
      timeline: {
        boardStartYear,
        activityBasedInventoryStartYear,
        scenarioStartYear,
        latestFinalisedInventoryYear,
        boardEndYear,
      },
      currency,
      measurementSystem,
      displayUnit,
      internationalisation: { defaultLanguage: primaryLanguage },
    } = clientBoardSettings;

    const defaultMeasurementSystem = measurementSystemToApiMeasurementSystem(this._loggingService, measurementSystem);
    const displayUnitExponent = displayUnitToDisplayUnitExponent(this._loggingService, displayUnit);
    const electricityEmissionFactorType = emissionFactorCalculationMethodToApiElectricityEmissionFactorType(
      clientBoardSettings.electricityEmissionFactorType
    );

    activityBasedInventoryStartYear ??= scenarioStartYear;
    latestFinalisedInventoryYear ??= scenarioStartYear;

    return {
      additionalLanguages: [], // not used right now in FE
      boardStartYear,
      activityBasedInventoryStartYear,
      baselineYear: scenarioStartYear,
      latestFinalisedInventoryYear,
      boardEndYear,
      currency,
      defaultMeasurementSystem,
      displayUnitExponent,
      primaryLanguage,
      electricityEmissionFactorType,
    };
  }

  clientBoardToUpdateEmissionStoryRequest(
    clientBoard: ClientBoardV2 | ClientBoard,
    language: Language
  ): ApiUpdateStoryRequest | ApiCreateStoryRequest {
    const description = isNotNullOrUndefined(clientBoard.boardSettings.emissionsPathways?.description)
      ? { [language]: clientBoard.boardSettings.emissionsPathways?.description }
      : {};
    /**
     * title might be an empty string, so we need to check for undefined and null
     */
    const title = isNotNullOrUndefined(clientBoard.boardSettings.emissionsPathways?.title)
      ? { [language]: clientBoard.boardSettings.emissionsPathways?.title }
      : {};
    const imageCredits = isNotNullOrUndefined(clientBoard.boardSettings.emissionsPathways?.imageCredit)
      ? { [language]: clientBoard.boardSettings.emissionsPathways?.imageCredit }
      : {};
    const imgUrl = clientBoard.boardSettings.emissionsPathways?.image ?? undefined;

    return {
      diagramDescription: {}, // has no customizable value today,
      description,
      diagramHeader: {}, // not customizable today
      historicalEmissions: {}, // root node has no historical emissions today
      imageCredits,
      imgUrl,
      title,
    };
  }

  clientBoardToUpdateMainStoryRequest(
    clientBoard: ClientBoardV2 | ClientBoard,
    language: Language
  ): ApiUpdateStoryRequest {
    const description =
      clientBoard.boardSettings.extendedDescription?.data?.texts?.[0]?.body !== undefined
        ? { [language]: clientBoard.boardSettings.extendedDescription?.data?.texts?.[0]?.body }
        : {};
    const diagramHeader = clientBoard.boardSettings.rootNodeProperties?.diagramHeader
      ? { [language]: clientBoard.boardSettings.rootNodeProperties?.diagramHeader }
      : {};
    const imageCredits = clientBoard.boardSettings.extendedDescription?.data?.images?.[0]?.imgCredits
      ? { [language]: clientBoard.boardSettings.extendedDescription?.data?.images?.[0]?.imgCredits }
      : {};
    /**
     * title might be an empty string, so we need to check for undefined and null
     */
    const title = isNotNullOrUndefined(clientBoard.boardSettings.extendedDescription?.data?.texts?.[0]?.title)
      ? { [language]: clientBoard.boardSettings.extendedDescription?.data?.texts?.[0]?.title }
      : {};
    const imgUrl = clientBoard.boardSettings.extendedDescription?.data?.images?.[0]?.imgURL ?? undefined;
    return {
      diagramDescription: {}, // it has no corresponding value today
      description,
      diagramHeader,
      imageCredits,
      title,
      imgUrl,
      historicalEmissions: {}, // root node has no historical emissions today
    };
  }

  toActivityGroupStoryRequest(language: string, story: Partial<StoryFormGroup>): CreateStoryRequest {
    const diagramHeader = isNotNullOrUndefined(story.diagramHeader) ? { [language]: story.diagramHeader } : undefined;
    const description = isNotNullOrUndefined(story.description) ? { [language]: story.description } : undefined;
    const imageCredits = isNotNullOrUndefined(story.imageCredits) ? { [language]: story.imageCredits } : undefined;
    const title = isNotNullOrUndefined(story.title) ? { [language]: story.title } : {};
    const imgUrl = story.imgUrl ?? undefined;
    return <any>{
      // having any here since api specs are not correctly generated
      description,
      diagramHeader,
      imageCredits,
      title,
      imgUrl,
    };
  }

  toClientBoardSettings(apiBoardSettings: ApiBoardSettingsResponse): ClientBoardSettings {
    return {
      ...apiBoardSettings,
      primaryLanguage: apiBoardSettings.primaryLanguage as Language,
      additionalLanguages: apiBoardSettings.additionalLanguages?.map((l) => l as Language) ?? [],
      currency: apiBoardSettingsToCurrency(this._loggingService, apiBoardSettings) ?? Currency.Euro,
    };
  }

  toCreateFeedbackSettings(board: ClientBoardV2): ApiCreateFeedbackSettingsRequest {
    const {
      boardSettings: {
        internationalisation: { defaultLanguage },
        feedbackSettings,
      },
    } = board;

    const { email, organisation, privacyNotice, privacyPolicyURL } = feedbackSettings
      ? feedbackSettings
      : { email: undefined, organisation: undefined, privacyNotice: undefined, privacyPolicyURL: undefined };
    const noticeDict = privacyNotice ? { [defaultLanguage]: privacyNotice } : undefined;

    if ([email, organisation, noticeDict, privacyPolicyURL].some((v) => !v)) {
      throw new Error('feedbackSettings is missing required fields');
    }
    return {
      email,
      organisation,
      privacyNotice: noticeDict,
      privacyPolicyUrl: privacyPolicyURL,
    } as ApiCreateFeedbackSettingsRequest;
  }

  toUpdateActionStatusRequest(
    name: string,
    status: ACTION_STATUS,
    language: Language,
    sortOrder?: number
  ): ApiUpdateActionStatusRequest {
    return {
      displayValue: { [language]: name },
      status,
      ...(sortOrder !== undefined ? { sortOrder } : {}),
    };
  }

  toUpdateActionTagRequest(
    name: string,
    language: Language,
    colour: ModelsActionTagColour,
    sortOrder?: number
  ): ApiUpdateActionTagRequest {
    return {
      displayValue: { [language]: name },
      colour,
      ...(sortOrder !== undefined ? { sortOrder } : {}),
    };
  }

  toUpdateActionTypeRequest(name: string, language: Language, sortOrder?: number): ApiUpdateActionTypeRequest {
    return {
      displayValue: { [language]: name },
      ...(sortOrder !== undefined ? { sortOrder } : {}),
    };
  }

  toUpdateDashboardSettingsRequest(board: ClientBoardV2): ApiUpdateDashboardSettingsRequest {
    const { boardSettings } = board;
    const {
      internationalisation: { defaultLanguage },
    } = boardSettings;
    const toLocalizedString = (string: string) => (typeof string === 'string' ? { [defaultLanguage]: string } : {});
    return {
      about: toLocalizedString(boardSettings.pointOfEntrySettings?.header?.about ?? ''),
      aboutTitle: toLocalizedString(boardSettings.pointOfEntrySettings?.header?.tagline ?? ''),
      actionSettingsLayout: boardSettings.defaultActionsView === DefaultActionsView.Column ? 'COLUMN' : 'LIST',
      actionSettingsShowCosts: boardSettings.showActionsCosts,
      actionSettingsShowUnconnected: boardSettings.showUnconnectedActions,
      footerBodyLeft: toLocalizedString(boardSettings.pointOfEntrySettings?.footer?.bodyLeft ?? ''),
      footerBodyRight: toLocalizedString(boardSettings.pointOfEntrySettings?.footer?.bodyRight ?? ''),
      goalDescription: toLocalizedString(boardSettings.pointOfEntrySettings?.header?.taglineSubText ?? ''),
      goalTitle: toLocalizedString(boardSettings.goalTitle ?? ''),
      pathwayGap: toLocalizedString(boardSettings.pathwayGap?.summary ?? ''),
      publicTabs: boardSettings.publicTabs,
      rootLocation: toLocalizedString(boardSettings.pointOfEntrySettings?.header?.country ?? ''),
      showReferenceScenarioAndAbatement: boardSettings.yearByYearDiagramLayout?.showBauAndAbatement ?? false,
      splashScreenBackgroundImageCredits: toLocalizedString(
        boardSettings.pointOfEntrySettings?.header?.backgroundImageCredits ?? ''
      ),
      splashScreenBackgroundImageUrl: boardSettings.pointOfEntrySettings?.header?.backgroundImage?.large ?? '',
      splashScreenDescription: toLocalizedString(boardSettings.introductionSummary ?? ''),
      splashScreenTitle: toLocalizedString(boardSettings.introductionTitle ?? ''),
      title: toLocalizedString(boardSettings.pointOfEntrySettings?.header?.boardTitle ?? ''),
      transitionElementLayout:
        boardSettings.transitionTargetNodes.layout === Layout.GRAPH_AND_BAROMETER
          ? 'GRAPH_AND_BAROMETER'
          : 'TARGET_AND_TEXT',
      transitionElementShowCardImages: boardSettings.transitionTargetNodes.showCardImages,
      primaryActionGrouping: boardSettings.primaryActionGrouping,
      secondaryActionGrouping: boardSettings.secondaryActionGrouping,
      actionSettingsDescription: toLocalizedString(boardSettings.actionSettingsDescription ?? ''),
      actionSettingsSubHeading: toLocalizedString(boardSettings.actionSettingsSubHeading ?? ''),
      emissionsTabHideBracket: boardSettings.emissionsTabHideBracket ?? false,
      emissionsTabHideReference: boardSettings.emissionsTabHideReference ?? false,
    };
  }

  toUpdateFeedbackSettingsRequest(boardSettings: BoardSettingsV2): ApiUpdateFeedbackSettingsRequest {
    const {
      internationalisation: { defaultLanguage },
      feedbackSettings,
    } = boardSettings;
    const toLocalizedString = (string: string) => (string.length ? { [defaultLanguage]: string } : {});
    return {
      ...(feedbackSettings?.email?.length ? { email: feedbackSettings?.email } : {}),
      ...(feedbackSettings?.organisation?.length ? { organisation: feedbackSettings?.organisation } : {}),
      ...(feedbackSettings?.privacyPolicyURL?.length ? { privacyPolicyUrl: feedbackSettings.privacyPolicyURL } : {}),
      privacyNotice: toLocalizedString(feedbackSettings?.privacyNotice ?? ''),
    };
  }

  toUpdateStakeholderRequest(
    name: string,
    stakeholder: STAKEHOLDER_TYPE_ALLOWED,
    language: Language,
    sortOrder?: number
  ): ApiUpdateStakeholderRequest {
    const toLocalizedString = (string: string) => (string.length ? { [language]: string } : {});
    return {
      name: toLocalizedString(name),
      stakeholder,
      ...(sortOrder ? { sortOrder } : {}),
    };
  }

  toUpdateV3RequiredFieldsRequest(board: ClientBoardV2): ApiUpdateV3RequiredFieldsRequest {
    const boardSettings = board.boardSettings as BoardSettingsV2 & {
      doNotDistributeUntargeted: boolean;
      unaddressedEmissionsDisplay: 'None' | 'TotalBarOnly' | 'TotalBarAndDiagrams';
      useNewTargetedEmissions: boolean;
      totalEmissions: number;
    };

    return {
      ...(boardSettings.doNotDistributeUntargeted !== undefined && boardSettings.doNotDistributeUntargeted !== null
        ? { doNotDistributeUntargetedEmissions: boardSettings.doNotDistributeUntargeted }
        : {}),
      ...(boardSettings.unaddressedEmissionsDisplay !== undefined && boardSettings.unaddressedEmissionsDisplay !== null
        ? { unaddressedEmissionsDisplay: boardSettings.unaddressedEmissionsDisplay }
        : {}),
      ...(boardSettings.useNewTargetedEmissions !== undefined && boardSettings.useNewTargetedEmissions !== null
        ? { useNewTargetedEmissions: boardSettings.useNewTargetedEmissions }
        : {}),
      ...(boardSettings.totalEmissions !== undefined && boardSettings.totalEmissions !== null
        ? { totalEmissions: boardSettings.totalEmissions }
        : {}),
    };
  }

  toClientActionFromActionResponse(action: ActionResponse, watchlists?: WatchlistResponse[]): ClientAction {
    const watchlist = watchlists?.find((watchlist) => watchlist.ownerId === action.id) ?? undefined;

    let _watchlistSpecifiecFields: Pick<ActionProperties, 'comment' | 'responsibleMember' | 'date'> = {};
    if (!!watchlist) {
      _watchlistSpecifiecFields = {
        comment: watchlist.comment,
        responsibleMember: watchlist.userIds,
        date: !!watchlist.date ? new Date(<string>watchlist.date) : null,
      };
    }

    let imgURL = '';
    if (!!action.images?.length) {
      imgURL = JSON.stringify(
        action.images?.map((image) => ({
          url: conditionallyPrefixImagesWithFilesUrl(image?.imageUrl, this._config.baseUrlFiles),
          credit: image?.credit || null,
          description: image?.description || null,
          id: image?.id || null,
        }))
      );
    }

    return <ClientAction>{
      id: action.id,
      title: action.title,
      actionProperties: {
        imgURL,
        implemented: action.implemented ? new Date(action.implemented) : undefined,
        impacted: action.impacted ? new Date(action.impacted) : undefined,
        ragRating: <RAGRating>action.ragRating,
        images: action.images?.map((image) => ({
          ...image,
          imageUrl: conditionallyPrefixImagesWithFilesUrl(image.imageUrl, this._config.baseUrlFiles),
        })),
        origin: action.actionOrigin?.id,
        type: action.actionType.id,
        swimlane: action.actionStatus.id,
        actionTags: (action.actionTags ?? []).map((t) => t.id),
        ...(!!action.cost?.id && action.cost.id !== EMPTY_GUID
          ? {
              costDistribution: {
                id: action.cost.id,
                totalCost: action.cost.value?.value ?? 0,
                totalCostInformation: action.cost.description,
                stakeholders:
                  ((action.cost.costProportions ?? []).map((costProportion) => ({
                    id: costProportion.stakeholder?.id,
                    name: costProportion.stakeholder?.name,
                    proportion: costProportion.proportion * 100,
                    type: <STAKEHOLDER_TYPE>costProportion.stakeholder?.stakeholder,
                  })) as StakeholderWithCostDistribution[]) ?? [],
                attributes: [],
              },
            }
          : {}),
        affected: action.affected,
        attributes: action.attributes,
        barriers: action.barriers,
        contribution: action.contribution,
        description: action.description,
        effect: action.effect,
        emissionsEnergy: {
          emissionReduction: action.emissionReduction,
          emissionsEnergyInformation: action.emissionEnergyInfo,
          energySavings: action.energySavings,
        },
        stakeholders: action.necessaryStakeholders,
        isWorkingMaterial: !action.public,
        reference: action.reference,
        accountable: action.responsibleOrganisation,
        watchlist: watchlist,
        readMore: linkingTextsToNamedLinks(action.linkingTexts ?? []),
        linkingTexts: action.linkingTexts ?? [],
        interventionDefinition: action.interventionDefinition,
        ..._watchlistSpecifiecFields,
      },
      _etag: '',
      _ts: 0,
      boardId: undefined as any,
      auxiliaryProperties: {},
      legacyCustomElementIds: action.legacyCustomElementIds ?? [],
      transitionElementIds: action.transitionElementIds ?? [],
    };
  }

  shouldPatchAction(a: Partial<ClientAction>): boolean {
    /**
     * Empty input-editor fields are null on save, so we need to specifically check for undefined on those fields
     */
    return (
      !!a.actionProperties?.swimlane ||
      !!a.actionProperties?.actionTags ||
      !!a.actionProperties?.type ||
      a.actionProperties?.affected !== undefined ||
      !!a.actionProperties?.attributes ||
      a.actionProperties?.barriers !== undefined ||
      a.actionProperties?.contribution !== undefined ||
      a.actionProperties?.description !== undefined ||
      a.actionProperties?.effect !== undefined ||
      a.actionProperties?.emissionsEnergy?.emissionsEnergyInformation !== undefined ||
      a.actionProperties?.emissionsEnergy?.emissionReduction !== undefined ||
      a.actionProperties?.emissionsEnergy?.energySavings !== undefined ||
      !!a.actionProperties?.impacted ||
      !!a.actionProperties?.implemented ||
      a.actionProperties?.stakeholders !== undefined ||
      a.actionProperties?.isWorkingMaterial !== undefined ||
      !!a.actionProperties?.ragRating ||
      a.actionProperties?.reference !== undefined ||
      /**
       * accountable might be an empty string, so we need to check for undefined and null
       */
      isNotNullOrUndefined(a.actionProperties?.accountable) ||
      !!a.title
    );
  }

  toLocalisedString = (string: string, lang: string) => (typeof string === 'string' ? { [lang]: string } : {});

  toCreateOrUpdateActionRequestFromClientAction(
    a: Partial<ClientAction>,
    lang: string
  ): UpdateActionRequest | CreateActionRequest {
    const body: any = {}; // Define the body as any object

    /**
     * Conditional assignments for all properties
     * Empty input-editor fields are null on save, so we need to specifically check for undefined on those fields
     */
    if (a.actionProperties?.swimlane) {
      body['actionStatusId'] = <string>a.actionProperties.swimlane;
    }

    body['actionOriginId'] = <string>a.actionProperties?.origin;

    if (a.actionProperties?.actionTags) {
      body['actionTagIds'] = a.actionProperties.actionTags;
    }
    if (a.actionProperties?.type) {
      body['actionTypeId'] = a.actionProperties.type;
    }
    if (a.actionProperties?.affected !== undefined) {
      body['affected'] = this.toLocalisedString(a.actionProperties.affected ?? '', lang);
    }
    if (a.actionProperties?.attributes) {
      body['attributes'] = a.actionProperties.attributes;
    }
    if (a.actionProperties?.barriers !== undefined) {
      body['barriers'] = this.toLocalisedString(a.actionProperties.barriers ?? '', lang);
    }
    if (a.actionProperties?.contribution !== undefined) {
      body['contribution'] = this.toLocalisedString(a.actionProperties.contribution ?? '', lang);
    }
    if (a.actionProperties?.description !== undefined) {
      body['description'] = this.toLocalisedString(a.actionProperties.description ?? '', lang);
    }
    if (a.actionProperties?.effect !== undefined) {
      body['effect'] = this.toLocalisedString(a.actionProperties.effect ?? '', lang);
    }
    if (a.actionProperties?.emissionsEnergy?.emissionsEnergyInformation !== undefined) {
      body['emissionEnergyInfo'] = this.toLocalisedString(
        a.actionProperties.emissionsEnergy.emissionsEnergyInformation ?? '',
        lang
      );
    }
    if (a.actionProperties?.emissionsEnergy?.emissionReduction !== undefined) {
      body['emissionReduction'] = a.actionProperties.emissionsEnergy.emissionReduction;
    }
    if (a.actionProperties?.emissionsEnergy?.energySavings !== undefined) {
      body['energySavings'] = a.actionProperties.emissionsEnergy.energySavings;
    }
    if (a.actionProperties?.impacted !== undefined) {
      body['impacted'] =
        a.actionProperties.impacted === null ? null : new Date(a.actionProperties.impacted).toISOString();
    }
    if (a.actionProperties?.implemented !== undefined) {
      body['implemented'] =
        a.actionProperties.implemented === null ? null : new Date(a.actionProperties.implemented).toISOString();
    }
    if (a.actionProperties?.stakeholders !== undefined) {
      body['necessaryStakeholders'] = this.toLocalisedString(a.actionProperties.stakeholders ?? '', lang);
    }
    if (a.actionProperties?.isWorkingMaterial !== undefined) {
      body['public'] = !a.actionProperties.isWorkingMaterial;
    }
    if (a.actionProperties?.ragRating) {
      body['ragRating'] = a.actionProperties.ragRating;
    }
    if (a.actionProperties?.reference !== undefined) {
      body['reference'] = this.toLocalisedString(a.actionProperties.reference ?? '', lang);
    }
    /**
     * accountable might be an empty string, so we need to check for undefined and null
     */
    if (isNotNullOrUndefined(a.actionProperties?.accountable)) {
      body['responsibleOrganisation'] = this.toLocalisedString(a.actionProperties.accountable ?? '', lang);
    }
    if (a.title) {
      body['title'] = this.toLocalisedString(a.title, lang);
    }
    if (a.actionProperties?.interventionDefinition?.id) {
      body['interventionDefinitionId'] = a.actionProperties.interventionDefinition.id;
    }
    if (a.transitionElementIds) {
      body['transitionElementIds'] = a.transitionElementIds;
    }
    if (a.legacyCustomElementIds) {
      body['legacyCustomElementIds'] = a.legacyCustomElementIds;
    }

    return body;
  }
}
